import { AxiosResponse } from "axios";
import { call, fork, put, takeLatest } from "redux-saga/effects";

import { NotificationSettings, NotificationSubscriptions } from "entities/notifications";
import { NotificationsApi } from "services/http";
import { hideModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";
import { getApiErrorMessage } from "utils/helpers";
import {
  SaveAllPayload,
  SaveSettingsPayload,
  SendWhatsappCodeActionPayload,
  VerifyWhatsappCodeActionPayload,
  getSettingsFailure,
  getSettingsRequest,
  getSettingsSuccess,
  getSubscriptionsFailure,
  getSubscriptionsRequest,
  getSubscriptionsSuccess,
  saveAllFailure,
  saveAllRequest,
  saveAllSuccess,
  saveSettingsFailure,
  saveSettingsRequest,
  saveSettingsSuccess,
  sendWhatsappCodeFailure,
  sendWhatsappCodeRequest,
  sendWhatsappCodeSuccess,
  verifyWhatsappCodeFailure,
  verifyWhatsappCodeRequest,
  verifyWhatsappCodeSuccess,
  whatsappSendRateLimited,
} from "store/slices/notificationSettingsSlice";

function* getSettingsFlow(action: { type: string; payload: number }) {
  try {
    const response: AxiosResponse<NotificationSettings> = yield call(
      NotificationsApi.getNotificationSettings,
      action.payload,
    );
    yield put(getSettingsSuccess(response.data));
  } catch (error) {
    yield put(getSettingsFailure(error as { detail: Record<string, string> }));
  }
}

function* watchGet() {
  yield takeLatest(getSettingsRequest.type, getSettingsFlow);
}

function* saveSettingsFlow(action: { type: string; payload: SaveSettingsPayload }) {
  const { userId, body } = action.payload;
  try {
    const response: AxiosResponse<NotificationSettings> = yield call(
      NotificationsApi.saveNotificationSettings,
      userId,
      body,
    );
    yield put(saveSettingsSuccess(response.data));
    yield put(
      showToast({
        id: `notification-settings-saved-${userId}`,
        message: "Notification settings saved.",
        type: "success",
      }),
    );
  } catch (error) {
    yield put(saveSettingsFailure(error as { detail: Record<string, string> }));
    yield put(
      showToast({
        id: `notification-settings-save-failed-${userId}`,
        message: getApiErrorMessage(error, "Failed to save notification settings. Please try again later."),
        type: "error",
      }),
    );
  }
}

function* watchSave() {
  yield takeLatest(saveSettingsRequest.type, saveSettingsFlow);
}

function* getSubscriptionsFlow(action: { type: string; payload: number }) {
  try {
    const response: AxiosResponse<NotificationSubscriptions> = yield call(
      NotificationsApi.getSubscriptions,
      action.payload,
    );
    yield put(getSubscriptionsSuccess(response.data));
  } catch (error) {
    yield put(getSubscriptionsFailure(error as { detail: Record<string, string> }));
  }
}

function* watchGetSubscriptions() {
  yield takeLatest(getSubscriptionsRequest.type, getSubscriptionsFlow);
}

function* saveAllFlow(action: { type: string; payload: SaveAllPayload }) {
  const { userId, channels, alertTypes, subscriptions } = action.payload;
  try {
    yield call(NotificationsApi.saveNotificationSettings, userId, channels);
    const settingsResponse: AxiosResponse<NotificationSettings> = yield call(
      NotificationsApi.setAlertTypes,
      userId,
      alertTypes,
    );
    const subscriptionsResponse: AxiosResponse<NotificationSubscriptions> = yield call(
      NotificationsApi.saveSubscriptions,
      userId,
      subscriptions,
    );
    yield put(saveAllSuccess({ settings: settingsResponse.data, subscriptions: subscriptionsResponse.data }));
    yield put(
      showToast({
        id: `notification-settings-saved-${userId}`,
        message: "Notification settings saved successfully.",
        type: "success",
      }),
    );
  } catch (error) {
    yield put(saveAllFailure(error as { detail: Record<string, string> }));
    yield put(
      showToast({
        id: `notification-settings-save-failed-${userId}`,
        message: "Failed to save notification settings. Please try again later.",
        type: "error",
      }),
    );
  }
}

function* watchSaveAll() {
  yield takeLatest(saveAllRequest.type, saveAllFlow);
}

function* sendWhatsappCodeFlow(action: { type: string; payload: SendWhatsappCodeActionPayload }) {
  const { userId, phone } = action.payload;
  try {
    yield call(NotificationsApi.sendWhatsappCode, userId, phone);
    yield put(sendWhatsappCodeSuccess());
  } catch (error) {
    const detail = (error as { detail?: { reason?: string; retry_after_seconds?: number } })?.detail;
    if (detail && typeof detail === "object" && detail.reason === "resend_limit") {
      yield put(whatsappSendRateLimited(detail.retry_after_seconds ?? 3600));
    } else {
      yield put(
        sendWhatsappCodeFailure(getApiErrorMessage(error, "Failed to send the verification code. Please try again.")),
      );
    }
  }
}

function* watchSendWhatsappCode() {
  yield takeLatest(sendWhatsappCodeRequest.type, sendWhatsappCodeFlow);
}

function* verifyWhatsappCodeFlow(action: { type: string; payload: VerifyWhatsappCodeActionPayload }) {
  const { userId, phone, code } = action.payload;
  try {
    const response: AxiosResponse<NotificationSettings> = yield call(
      NotificationsApi.verifyWhatsappCode,
      userId,
      phone,
      code,
    );
    yield put(verifyWhatsappCodeSuccess(response.data));
    yield put(hideModal());
    yield put(
      showToast({
        id: `whatsapp-verified-${userId}`,
        message: "WhatsApp number verified successfully.",
        type: "success",
      }),
    );
  } catch (error) {
    yield put(verifyWhatsappCodeFailure(getApiErrorMessage(error, "Invalid or expired code. Please try again.")));
  }
}

function* watchVerifyWhatsappCode() {
  yield takeLatest(verifyWhatsappCodeRequest.type, verifyWhatsappCodeFlow);
}

export const notificationSettingsWatchers = [
  fork(watchGet),
  fork(watchSave),
  fork(watchGetSubscriptions),
  fork(watchSaveAll),
  fork(watchSendWhatsappCode),
  fork(watchVerifyWhatsappCode),
];
